/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import React from 'react';
import { isEqual } from 'lodash';
import { Container } from 'flux/utils';

import { prefillMessageWithGroupName } from 'utils/prefillMessageWithGroupName';
import AnnouncementComposer from 'groups/components/AnnouncementComposer.jsx';
import ConversationHeader from 'chat/components/ConversationHeader.jsx';
import MessageList from 'chat/components/MessageList.jsx';
import ChatConstants from 'chat/constants/ChatConstants';
import ChatStore from 'chat/stores/ChatStore';
import CurrentUser from 'auth/CurrentUser';
import { Message, GapMessage, TARGET_TYPES } from 'entities/Message';
import Group from 'entities/Group';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import { membershipDescriptionFor } from 'groups/lib/groupDisplay';
import MessageActions from 'groups/actions/MessageActions';
import MessageStore from 'groups/stores/MessageStore';
import { getGroupDescription, getGroupKey, EVERYONE } from 'utils/roleHelpers';
import mountTymkPrompt from 'utils/mountTymkPrompt';

import type { MessageType } from 'chat/lib/MessageInterfacePropType';

import './styles/AnnouncementsConversationView.scss';

type State = {
  gap: ?GapMessage | ?Message,
  loading: boolean,
  messages: MessageType[],
};

type Props = {
  group: Group,
  params: Object,
  className?: ?string,
};

class AnnouncementsConversationView extends React.Component {
  props: Props;
  state: State;

  static getStores() {
    return [MessageStore];
  }

  static calculateState(prevState: ?State, props: Props): State {
    const { params, group } = props;
    const { role } = params;

    const groupKey = getGroupKey(group.id, role);
    const messages = MessageStore.getMessages(groupKey).models.slice(0).reverse();

    const gap = MessageStore.getGap(groupKey);
    if (gap) {
      messages.unshift(new GapMessage());
    }
    const thread = group.getThread(role);
    return {
      gap,
      messages,
      // May be a fake promo thread, in which case we have nothing to load
      loading: !!thread && !MessageStore.isDoneLoadingForGroup(groupKey),
    };
  }

  componentDidMount() {
    const { group } = this.props;
    this.loadMessages(group);
    this.focusComposer();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    // shallow state
    const shallowKeys = [
      'loading',
    ];

    for (const key of shallowKeys) {
      if (this.state[key] !== nextState[key]) {
        return true;
      }
    }

    if (!isEqual(this.props.params, nextProps.params)) {
      return true;
    }

    if (this.state.messages.length !== nextState.messages.length) {
      return true;
    }

    const gapPresenceChanged = this.state.gap !== nextState.gap;
    const gapValueChanged = this.state.gap && nextState.gap && this.state.gap.id !== nextState.gap.id;

    if (gapPresenceChanged || gapValueChanged) {
      return true;
    }

    return false;
  }

  componentDidUpdate(prevProps: Object) {
    const currentGroup = this.props.group;
    const roleChanged = prevProps.params.role !== this.props.params.role;
    const groupChanged = currentGroup !== prevProps.group;

    if (roleChanged || groupChanged) {
      this.loadMessages(currentGroup);
    }
    this.focusComposer();
  }

  getInitialAnnouncement = (): Message => {
    const { group } = this.props;
    const groupKey = getGroupKey(group.id, this.props.params.role);
    const onEveryoneThread = this.props.params.role === EVERYONE;
    if (onEveryoneThread && MessageStore.getMessages(groupKey).isEmpty()) {
      const groupName = group.getName();
      const initialText = prefillMessageWithGroupName(groupName);
      return Message.createMessage(null, initialText);
    } else {
      return new Message();
    }
  };

  loadMessages = (group: Group) => {
    if (group.isRoot()) {
      return;
    }

    const { role } = this.props.params;
    const thread = group.getThread(role);

    if (thread) {
      const threadUuid = thread.uuid;
      MessageActions.loadThreadMessages(group.id, threadUuid, role);
      if (group.getPostableRoles().length) {
        MessageActions.loadScheduledThreadMessages(group.id, threadUuid, role);
      }
    }
  };

  loadMoreMessages = () => {
    const { group, params } = this.props;
    const { role } = params;

    if (this.state.gap) {
      const gapParams = this.state.gap.getGapParams();
      MessageActions.loadThreadMessages(
        group.id,
        group.getThread(role).uuid,
        role,
        {
          max_seq: gapParams['max_seq'],
          since_seq: gapParams['since_seq'],
        },
      );
    }
  };

  focusComposer = () => {
    if (ChatStore.getFocusView() === ChatConstants.FocusableViews.COMPOSER
        && this.refs.composer) {
      this.refs.composer.focus();
    }
  };

  onMessageSent = () => {
    if (this.refs.messageList) {
      this.refs.messageList.scrollToBottom();
    }
    mountTymkPrompt(this, 'send_announcement');
  };

  render() {
    const { group, params } = this.props;
    const { role } = params;
    const { messages } = this.state;

    const loadingIndicator = (
      <div className="AnnouncementsConversationView-loadingContainer">
        <LoadingIndicator className="AnnouncementsConversationView-loading" size="large" />
      </div>
    );

    const target = {
      name: group.getName(),
      type: TARGET_TYPES.GROUP_TARGET,
      role,
      group_id: group.id,
    };

    const title = getGroupDescription(group, role);

    return (
      <div className={cx('AnnouncementsConversationView', this.props.className)}>
        <ConversationHeader
          ref="header"
          title={title}
          subtitle={'Announcements to ' + membershipDescriptionFor(group, role)}
        />
        <div className="AnnouncementsConversationView-body">
          {this.state.loading ?
            loadingIndicator :
            (
            <div className="AnnouncementsConversationView-body-wrap">
              <MessageList
                ref="messageList"
                className="AnnouncementsConversationView-body-messageList"
                unreadCount={0}
                group={group}
                messages={messages}
                loadMoreMessages={this.loadMoreMessages}
                subgroupRole={this.props.params.role}
              />
              <AnnouncementComposer
                ref="composer"
                initialMessage={this.getInitialAnnouncement()}
                className="AnnouncementsConversationView-body-composer"
                targets={[target]}
                hideOldInput
                inAnnouncementsThread
                charLimit={CurrentUser.getUser().getAnnouncementCharactersLimit()}
                charLimitWithFiles={
                  CurrentUser.getUser().getAnnouncementCharactersLimitWithFiles()
                }
                onMessageSent={this.onMessageSent}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Container.create(AnnouncementsConversationView, {
  withProps: true,
  pure: false,
});



// WEBPACK FOOTER //
// ./src/chat/components/AnnouncementsConversationView.jsx